/**
 * Générateur — Il mène des calculs sur des grandeurs mesurables, notamment
 * des grandeurs composées, et exprime les résultats dans les unités adaptées.
 * Attendu ID : 233 | Niveau : 3ème | Domaine : Grandeurs (d18, O3)
 *
 * Source doc prof (CS3). Plan auto-validé 2026-07-17 (TODO §6) :
 * « piscine » (volume → litres → durée de remplissage : la chaîne complète),
 * « massevolumique » (kg/m³ → g/cm³ + masse d'un volume), « energie »
 * (E = P × t, le piège 2 h 45 = 2,75 h). Valeurs EXACTES par construction.
 */

import { randomChoice, randInt, tex, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 233;
const L_PISCINE = 'Du volume à la durée de remplissage (débit)';
const L_MASSEVOL = 'Masse volumique : convertir puis calculer une masse';
const L_ENERGIE = 'Énergie = puissance × temps (heures décimales)';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 1000) / 1000).replace('.', ',');

// ═════════ « piscine » ═════════

const genPiscineTrous = () => {
    let L; let l; let p10; let debit; let volumeL; let heures;
    for (let essai = 0; essai < 80; essai++) {
        L = randomChoice([20, 25, 40, 50]);
        l = randomChoice([8, 10, 12, 15, 20]);
        p10 = randomChoice([12, 15, 16, 18, 20]);          // profondeur en dixièmes de m
        debit = randomChoice([4000, 5000, 6000, 8000, 10000]);   // L/h
        volumeL = L * l * p10 * 100;                       // m³ ×1000 = L ; p = p10/10
        heures = volumeL / debit;
        if (Number.isInteger(heures) && heures >= 10 && heures <= 200) break;
        heures = null;
    }
    if (heures === null) { L = 25; l = 10; p10 = 16; debit = 5000; volumeL = 400000; heures = 80; }
    const phrase = `$\\text{Volume : } {{${frNb((L * l * p10) / 10)}}}\\ \\text{m}^{3} = {{${volumeL}}}\\ \\text{L}`
        + ` \\qquad \\text{Durée : } {{${heures}}}\\ \\text{h}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une piscine mesure ${tex(`${L} \\times ${l}`)} m, avec une profondeur moyenne de `
        + `${frNb(p10 / 10)} m. On la remplit avec une pompe de ${debit} L/h.</p>`
        + '<p>Rappel : 1 m³ = 1 000 L.</p>'
        + '<p><b>Calcule le volume en m³, convertis-le en litres, puis donne la durée de remplissage '
        + 'en heures.</b> Écris les entiers sans espaces.</p>',
        { ...opts(L_PISCINE), mode: 'saisie' });
    payload._meta.params = { L, l, p10, debit, volumeL, heures };
    return payload;
};

// ═════════ « massevolumique » ═════════

const genMasseVolTrous = () => {
    let rhoMil; let v10; let masse100;
    for (let essai = 0; essai < 80; essai++) {
        rhoMil = randomChoice([1250, 1879, 2700, 5640, 7850, 8960, 11400]);  // kg/m³ (= g/dm³·... )
        v10 = randInt(12, 85);                             // volume en dixièmes de dm³
        masse100 = rhoMil * v10;                           // masse en (kg/m³)·(dm³/10) → g·... voir ci-dessous
        // masse (kg) = rho(kg/m³) × v(m³) = rho × v10/10 000 → exacte au dixième si divisible
        if ((rhoMil * v10) % 1000 === 0) break;
        masse100 = null;
    }
    if (masse100 === null) { rhoMil = 2700; v10 = 50; }
    const masseKg = (rhoMil * v10) / 10000;                // kg exact
    const gcm3 = frNb(rhoMil / 1000);
    const phrase = `$\\text{Masse volumique : } {{${gcm3}}}\\ \\text{g/cm}^{3}`
        + ` \\qquad \\text{Masse : } {{${frNb(masseKg)}}}\\ \\text{kg}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un métal a une masse volumique de ${rhoMil} kg/m³.</p>`
        + '<p>Rappel : 1 dm³ = 1 000 cm³.</p>'
        // « (÷ 1 000) » donnait l'opération de conversion à trouver.
        + `<p><b>Exprime-la en g/cm³, puis calcule la masse, en kg, de ${frNb(v10 / 10)} dm³ `
        + 'de ce métal.</b> Utilise une virgule pour les décimaux.</p>',
        { ...opts(L_MASSEVOL), mode: 'saisie' });
    payload._meta.params = { rhoMil, v10, masseKg };
    return payload;
};

// ═════════ « energie » — E = P × t ═════════

const genEnergieTrous = () => {
    let P; let h; let quart; let wh;
    for (let essai = 0; essai < 60; essai++) {
        P = randomChoice([60, 120, 180, 200, 240, 400, 800]);
        h = randInt(1, 4);
        quart = randomChoice([15, 30, 45]);                // minutes
        wh = P * (h + quart / 60);
        if (Number.isInteger(wh)) break;
        wh = null;
    }
    if (wh === null) { P = 180; h = 2; quart = 45; wh = 495; }
    const tDec = frNb(h + quart / 60);
    const phrase = `$t = {{${tDec}}}\\ \\text{h} \\qquad E = {{${wh}}}\\ \\text{Wh}`
        + ` = {{${frNb(wh / 1000)}}}\\ \\text{kWh}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un appareil de puissance P = ${P} W fonctionne pendant ${h} h ${quart} min. `
        + `L'énergie consommée vaut ${tex('E = P \\times t')}, avec t exprimé en heures.</p>`
        // Le « piège : 2 h 45 min ≠ 2,45 h ! » annonçait l'erreur à éviter, donc
        // supprimait l'obstacle que l'exercice construit.
        + '<p><b>Convertis la durée en heures décimales, puis calcule E en Wh et en kWh.</b></p>',
        { ...opts(L_ENERGIE), mode: 'saisie' });
    payload._meta.params = { P, h, quart, wh };
    return payload;
};

const GENERATORS = {
    piscine_texte_trou_saisie: genPiscineTrous,
    massevolumique_texte_trou_saisie: genMasseVolTrous,
    energie_texte_trou_saisie: genEnergieTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'piscine', label: L_PISCINE, formatsList: buildFormats('piscine') },
    { id: 'massevolumique', label: L_MASSEVOL, formatsList: buildFormats('massevolumique') },
    { id: 'energie', label: L_ENERGIE, formatsList: buildFormats('energie') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
